import { node, shape, string } from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { TipWrap } from '../styles-and-themes/styles/styled';
import Language from './Language/Language';
import MLIcon from './controls/MLIcon/MLIcon';

const Tooltip = ({ id, tooltipContent, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const delayTimerRef = useRef(null);

  const escKeyCloseHandler = (event) => {
    if (event.key === 'Escape') {
      setIsFocused(false);
      setIsHovered(false);
    }
  };

  useEffect(
    () => {
      document.addEventListener('keydown', escKeyCloseHandler, false);
      return () => {
        document.removeEventListener('keydown', escKeyCloseHandler, false);
      };
    },
    [escKeyCloseHandler],
  );

  useEffect(() => {
    setIsVisible(isHovered || isFocused);
  }, [isHovered, isFocused]);

  // Clean up timer when component unmounts
  useEffect(() => () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
    }
  }, []);

  // Prevent the tooltip from closing when moving from the target to the tooltip content
  const handleMouseLeaveWithDelay = () => {
    delayTimerRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  const handleMouseEnter = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
    }
    setIsHovered(true);
  };

  return (
    <TipWrap
      id={`${id}_container`}
      className="tooltip"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeaveWithDelay}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    >
      {children}
      <span
        id={`${id}_content`}
        className="tooltipContent"
        role="tooltip"
        style={{
          visibility: isVisible ? 'visible' : 'hidden',
          opacity: isVisible ? 1 : 0,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setIsHovered(false)}
      >
        {tooltipContent}
      </span>
    </TipWrap>
  );
};

Tooltip.propTypes = {
  id: string.isRequired,
  tooltipContent: node.isRequired,
  children: node.isRequired,
};

export const PasswordTooltip = ({ id }) => (
  <Tooltip
    id={id}
    tooltipContent={(
      <>
        Passwords must contain at least:
        <br />
        8 - 72 characters
        <br />
        1 lowercase letter
        <br />
        1 uppercase letter
        <br />
        1 number
        <br />
        1 special character: ! @ # $ % ^ &amp; * ( ) _ + | ~ - = \ ` &#123; &#125; [ ] : &#34; ; &#39; &lt; &gt; ? , /
      </>
  )}
  >
    <div id={`${id}_activator`} role="button" tabIndex={0} aria-describedby={`${id}_content`} aria-label="Password tips">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <g>
          <path d="M12,2c-5.486,0 -10,4.514 -10,10c0,5.486 4.514,10 10,10c5.486,0 10,-4.514 10,-10c0,-5.486 -4.514,-10 -10,-10Zm0,18c-4.389,0 -8,-3.611 -8,-8c0,-4.389 3.611,-8 8,-8c4.389,0 8,3.611 8,8c0,4.389 -3.611,8 -8,8l0,0Z" fillRule="nonzero" />
          <rect height="7.253" width="2" x="11" y="9.747" />
          <rect height="2" width="2" x="11" y="6.747" />
        </g>
      </svg>
    </div>
  </Tooltip>
);

PasswordTooltip.propTypes = {
  id: string,
};

PasswordTooltip.defaultProps = {
  id: 'pwTooltip',
};

export const EmailTooltip = ({
  id,
  brand,
}) => (
  <Tooltip
    id={id}
    tooltipContent={
      <Language category="copy" which="emailTooltip" brand={brand} />
  }
  >
    <div id={`${id}_activator`} role="button" tabIndex={0} aria-describedby={`${id}_content`} aria-label="Email address tips">
      <MLIcon type="info_outline" aria-hidden />
    </div>
  </Tooltip>
);

EmailTooltip.propTypes = {
  brand: shape({
    short: string.isRequired,
    full: string.isRequired,
  }).isRequired,
  id: string,
};

EmailTooltip.defaultProps = {
  id: 'emailTooltip',
};
